import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { Chart as AstroChartCanvas } from '@astrodraw/astrochart';
import { NatalChartData } from '../types/astrological-chart';
import useHoroscopeApi from '../api/useHoroscopeApi';
import { zodiacHebrewNames } from '../services/zodiacService';

interface NatalChartProps {
  userId: string;
  size?: string;
}

const NatalChartVisualizer: React.FC<NatalChartProps> = ({ userId, size = '100%' }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [natalChartData, setNatalChartData] = useState<NatalChartData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [chartSize, setChartSize] = useState(0);
  const { getNatalChartData } = useHoroscopeApi();

  useLayoutEffect(() => {
    const updateSize = () => {
      if (chartContainerRef.current) {
        setChartSize(chartContainerRef.current.offsetWidth);
      }
    };

    window.addEventListener('resize', updateSize);
    updateSize(); // Set initial size

    return () => window.removeEventListener('resize', updateSize);
  }, [loading]);

  useEffect(() => {
    const fetchNatalChartData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getNatalChartData({ id: userId });
        setNatalChartData(data);
      } catch (err) {
        console.error("Failed to fetch natal chart data:", err);
        setError("Failed to load natal chart data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchNatalChartData();
  }, [userId, getNatalChartData]);

  useEffect(() => {
    if (natalChartData && chartContainerRef.current && chartSize > 0) {
      try {
        chartContainerRef.current.innerHTML = '';

        const chart = new AstroChartCanvas(
          chartContainerRef.current.id || 'chart-container',
          chartSize,
          chartSize
        );

        // Transform the data into the format expected by @astrodraw/astrochart
        const chartData = {
          planets: natalChartData.planets.reduce((acc, planet) => {
            // Convert planet name to proper case for the library
            const planetName = planet.name.charAt(0).toUpperCase() + planet.name.slice(1).toLowerCase();
            acc[planetName] = [
              planet.longitude, // longitude
              0, // latitude
              1, // distance
              0, // speed
              planet.house || 0, // house (default to 0 if not provided)
              planet.is_retrograde ? 1 : 0 // retrograde flag
            ];
            return acc;
          }, {} as Record<string, number[]>),
          cusps: Array(12).fill(0).map((_, i) => {
            const house = natalChartData.houses.find(h => h.house === i + 1);
            return house ? house.cusp_longitude : 0;
          }),
          aspects: natalChartData.aspects.map(aspect => ({
            planet1: aspect.planet1.charAt(0).toUpperCase() + aspect.planet1.slice(1),
            planet2: aspect.planet2.charAt(0).toUpperCase() + aspect.planet2.slice(1),
            type: aspect.type,
            orb: aspect.orb
          }))
        };

        chart.radix(chartData);

      } catch (e) {
        console.error("Error drawing chart with @astrodraw/astrochart:", e);
        setError("Error rendering natal chart visualization. Please check console for details.");
      }
    }
  }, [natalChartData, chartSize]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>טוען מפה אסטרולוגית...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 2, m: 2, backgroundColor: 'error.light', color: 'white' }}>
        <Typography variant="h6">שגיאה</Typography>
        <Typography variant="body2">{error}</Typography>
      </Paper>
    );
  }

  if (!natalChartData) {
    return (
      <Paper elevation={3} sx={{ p: 2, m: 2 }}>
        <Typography variant="body1">לא נמצאו נתונים למפה האסטרולוגית שלך.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 2, m: 2 }}>
      <Typography variant="h5" component="h2" gutterBottom>
      המפה האסטרולוגית שלך בעת הלידה
      </Typography>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div ref={chartContainerRef} id="chart-container" style={{ width: size, height: 'auto', aspectRatio: '1/1' }}>
          {/* The chart will be drawn inside this div by the library */}
        </div>
      </Box>
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">מיקומים מרכזיים:</Typography>
        <Typography variant="body1">
          השמש ב{zodiacHebrewNames[natalChartData.planets.find(p => p.name.toLowerCase() === 'sun')?.sign.toLowerCase() || '']}
        </Typography>
        <Typography variant="body1">
          הירח ב{zodiacHebrewNames[natalChartData.planets.find(p => p.name.toLowerCase() === 'moon')?.sign.toLowerCase() || '']}
        </Typography>
        <Typography variant="body1">
          אופק ב{zodiacHebrewNames[natalChartData.houses.find(h => h.house === 1)?.sign.toLowerCase() || '']}
        </Typography>
        <Typography variant="body1">
          רום שמיים ב{zodiacHebrewNames[natalChartData.houses.find(h => h.house === 10)?.sign.toLowerCase() || '']}
        </Typography>
      </Box>
    </Box>
  );
};

export default NatalChartVisualizer;